import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { parseError } from '@/lib/errors'
import { PipelineSection, useUpdateLeadPipelineFields } from '@/models/myPipeline'
import { salesLeadStatusLabels, useSalesLeads } from '@/models/salesLead'

const COPY = {
  [PipelineSection.DEAL]: { button: 'Add deal', title: 'Add a deal' },
  [PipelineSection.WARM]: { button: 'Add warm account', title: 'Add a warm account' },
}

interface AddToPipelineDialogProps {
  /** Rep whose pipeline is being viewed; only their own leads are offered. */
  repId: string
  section: PipelineSection.DEAL | PipelineSection.WARM
  /** The viewed month, "yyyy-MM-01"; a new deal is expected to close in it. */
  month: string
  primary?: boolean
}

/** Puts one of the rep's own leads on the page as a deal or a warm account; its status is left alone. */
export function AddToPipelineDialog({ repId, section, month, primary = false }: AddToPipelineDialogProps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [debouncedValue, setDebouncedValue] = useState('')
  const update = useUpdateLeadPipelineFields()
  const { leads, isLoading } = useSalesLeads({
    assignedRep: repId,
    search: debouncedValue.trim() || undefined,
  })

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), 250)
    return () => clearTimeout(timer)
  }, [value])

  const add = (leadId: string) => {
    update.mutate(
      section === PipelineSection.DEAL
        ? { id: leadId, pipelineSection: section, expectedCloseMonth: month }
        : { id: leadId, pipelineSection: section },
      {
        onSuccess: () => {
          setOpen(false)
          setValue('')
        },
        onError: (error) => toast.error(parseError(error)),
      }
    )
  }

  return (
    <>
      <Button variant={primary ? 'default' : 'outline'} onClick={() => setOpen(true)}>
        {COPY[section].button}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{COPY[section].title}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Search your leads by name or DOT"
          />
          <div className="flex max-h-80 flex-col gap-1 overflow-y-auto">
            {isLoading && <p className="text-muted-foreground p-2 text-sm">Searching…</p>}
            {!isLoading && leads.length === 0 && (
              <p className="text-muted-foreground p-2 text-sm">No leads of yours match that search.</p>
            )}
            {leads.map((lead) => (
              <Button
                key={lead.id}
                variant="ghost"
                disabled={update.isPending}
                className="h-auto justify-start py-2 text-left"
                onClick={() => add(lead.id)}
              >
                <span>
                  <span className="block font-medium">{lead.legalName}</span>
                  <span className="text-muted-foreground block text-xs">
                    {lead.dotNumber ? `DOT ${lead.dotNumber}` : 'No DOT'} ·{' '}
                    {salesLeadStatusLabels[lead.status]}
                  </span>
                </span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
